import React, { useRef } from 'react';
import ContactForm from '../../../components/ContactForm';
import type { ContactFormData } from '../../../api/submitContactForm';
import I3PageHero from '../components/I3PageHero';
import { useRevealSections } from '../hooks/useReveal';
import '../styles/I3Page.css';
import '../styles/I3Contact.css';
import { getI3ContactServiceOptions } from '../data/i3ContactServiceOptions';

const I3_EMAIL = 'info@i3coachingconsulting.com';

const I3_CONTACT = {
  email: I3_EMAIL,
  phone: undefined as string | undefined,
  location: undefined as string | undefined,
};

/** Formspree form endpoint — notifications go to the inbox configured in Formspree. */
const I3_FORMSPREE_ENDPOINT =
  process.env.REACT_APP_I3_FORMSPREE_URL || 'https://formspree.io/f/mjgjrqng';

async function submitToFormspree(data: ContactFormData): Promise<{ success: boolean; message: string }> {
  try {
    const res = await fetch(I3_FORMSPREE_ENDPOINT, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: data.name.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        company: data.company.trim(),
        message: data.message.trim(),
        interested_services: data.interestedServices?.trim() || '',
        _subject: 'Contact from I-3 Coaching & Consulting website',
        _replyto: data.email.trim(),
      }),
    });

    const payload = (await res.json().catch(() => null)) as { error?: string; errors?: unknown } | null;

    if (res.ok) {
      return { success: true, message: '' };
    }

    const msg =
      (payload && typeof payload.error === 'string' && payload.error) ||
      'Could not send your message. Please try again or email us directly.';
    return { success: false, message: msg };
  } catch {
    return { success: false, message: 'Network error. Please try again or email us directly.' };
  }
}

const I3Contact: React.FC = () => {
  const wrapRef = useRef<HTMLDivElement>(null);
  useRevealSections(wrapRef);

  return (
    <article className="i3-contact">
      <I3PageHero showLogo title="Contact" subtitle="We'd love to hear from you" />
      <div className="i3-page-wrap" ref={wrapRef}>
        <section className="i3-section i3-contact-section i3-reveal">
          <div className="i3-card i3-contact-card">
            <ContactForm
              variant="i3"
              onSubmit={submitToFormspree}
              serviceOptions={getI3ContactServiceOptions()}
              servicesFieldLabel="What services are you interested in?"
              showContactInfo={true}
              contactInfo={{
                email: I3_CONTACT.email,
                phone: I3_CONTACT.phone || undefined,
                location: I3_CONTACT.location || undefined,
              }}
            />
          </div>
          <p className="i3-contact-reassurance">We typically respond within 24–48 hours.</p>
        </section>
      </div>
    </article>
  );
};

export default I3Contact;
